import { useState, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { MaterialContext } from "../context/MaterialContext";
import MaterialViewCount from "../utilities/MaterialViewCount";
import modalStyle from "../styles/modal.module.scss";
import ImageDownload from "../utilities/ImageDownload";
import { trackEvent } from "../utilities/analytics";
import MobileMaterialModal from "./mobile/mobileMaterialModal";
import MaterialDetailLayout from "./MaterialDetailLayout";
import LoadingDots from "./LoadingDots";
import { isVendorUser } from "../utilities/AuthState";
import { isDesignerHiddenStatus } from "../utilities/MaterialDetails";
import { lockBodyScroll, unlockBodyScroll } from "../utilities/lockBodyScroll";

function MaterialModal({
  showModal,
  setShowModal,
  id,
  brandData,
  userInformation,
  media,
  imageNum,
}) {
  const navigate = useNavigate();
  const { ROOT_SERVER, allMaterials } = useContext(MaterialContext);
  const { collection, changeCollection } = useContext(UserContext);
  const [materialData, setMaterialData] = useState([]);
  const [viewing, setViewing] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageNumber, setImageNumber] = useState(imageNum);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [imageDownload, setImageDownload] = useState(false);
  const [isTabletPortrait, setIsTabletPortrait] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return (
      window.matchMedia("(orientation: portrait)").matches &&
      window.matchMedia("(min-width: 761px) and (max-width: 1000px)").matches
    );
  });
  const [isLandscapePhone, setIsLandscapePhone] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return (
      window.matchMedia("(orientation: landscape)").matches &&
      window.matchMedia("(max-width: 1100px)").matches &&
      window.matchMedia("(pointer: coarse)").matches
    );
  });
  const didTrackOpen = useRef(false);

  const useMobileModal =
    media === "mobile" ||
    isTabletPortrait ||
    isLandscapePhone ||
    (typeof window !== "undefined" && window.innerWidth <= 760);

  useEffect(() => {
    setImageNumber(imageNum || 0);
  }, [imageNum]);

  useEffect(() => {
    setImageLoaded(false);
  }, [viewing]);

  useEffect(() => {
    const updateModalDeviceMode = () => {
      if (typeof window === "undefined") {
        return;
      }

      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;
      const isTabletWidth = window.matchMedia(
        "(min-width: 761px) and (max-width: 1000px)",
      ).matches;
      const isPhoneWidth = window.matchMedia("(max-width: 1100px)").matches;
      const isTouch = window.matchMedia("(pointer: coarse)").matches;

      setIsTabletPortrait(isPortrait && isTabletWidth);
      setIsLandscapePhone(isLandscape && isPhoneWidth && isTouch);
    };

    updateModalDeviceMode();
    window.addEventListener("resize", updateModalDeviceMode);
    window.addEventListener("orientationchange", updateModalDeviceMode);

    return () => {
      window.removeEventListener("resize", updateModalDeviceMode);
      window.removeEventListener("orientationchange", updateModalDeviceMode);
    };
  }, []);

  useEffect(() => {
    if (id && showModal) {
      setError(false);
      const getMaterial = allMaterials.find((material) => material.id === id);
      if (
        getMaterial &&
        (isVendorUser(userInformation) ||
          !isDesignerHiddenStatus(getMaterial.status))
      ) {
        const materialImages = Array.isArray(getMaterial.images)
          ? getMaterial.images
          : [];
        const safeImage =
          materialImages[imageNumber] || materialImages[0] || "";

        setMaterialData(getMaterial);
        setViewing(
          `${ROOT_SERVER}${getMaterial.vendor}/${getMaterial.slug}/${
            useMobileModal ? "swatch" : "full"
          }/${safeImage}`,
        );
        if (!didTrackOpen.current) {
          didTrackOpen.current = true;
          trackEvent("material_modal_open", {
            vendor: getMaterial.vendor,
            slug: getMaterial.slug,
          });
        }
        setLoaded(true);
      } else {
        setError(true);
      }
    }
  }, [allMaterials, ROOT_SERVER, id, imageNumber, showModal, useMobileModal, userInformation]);

  useEffect(() => {
    if (!showModal) {
      return;
    }

    document.body.classList.add("material-modal-open");
    if (useMobileModal) {
      document.body.classList.add("material-modal-open-mobile");
    } else {
      document.body.classList.remove("material-modal-open-mobile");
    }
    lockBodyScroll();

    return () => {
      document.body.classList.remove("material-modal-open");
      document.body.classList.remove("material-modal-open-mobile");
      unlockBodyScroll();
    };
  }, [showModal, useMobileModal]);

  useEffect(() => {
    if (!showModal) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowModal(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [setShowModal, showModal]);

  useEffect(() => {
    MaterialViewCount(materialData.id, userInformation);
  }, [materialData.id, userInformation]);

  const handleCloseModal = () => {
    setShowModal(false);
  };
  const handleImageLoaded = () => {
    setImageLoaded(true);
  };

  const handleChangeImage = (i) => {
    if (i === imageNumber) {
      return;
    }
    setImageLoaded(false);
    setImageNumber(i);
  };
  const handleSingle = (vendor, slug) => {
    navigate(`/${vendor}/${slug}`);
    setShowModal(false);
  };
  const handleEdit = (vendor, slug) => {
    navigate(`/material/edit/${vendor}/${slug}`);
    setShowModal(false);
  };

  const canEdit = isVendorUser(userInformation);

  const handleDownloadImage = (vendor, slug) => {
    ImageDownload(viewing, vendor, slug);
    trackEvent("image_download", { vendor, slug });
    setImageDownload(true);
    setTimeout(() => {
      setImageDownload(false);
    }, 3000);
  };

  const { title, slug, description, images, vendor } = materialData;

  const materialImages = Array.isArray(images) ? images : [];
  const inCollection =
    Array.isArray(collection) && collection.includes(materialData?.id);

  if (!showModal) {
    return null;
  }

  const modalRoot =
    typeof document === "undefined" ? null : document.body;

  const portalModal = (node) =>
    modalRoot ? createPortal(node, modalRoot) : node;

  if (useMobileModal) {
    if (error) {
      return portalModal(
        <div className={modalStyle.modalShade} onClick={handleCloseModal}>
          <div className={modalStyle.modalStateCard}>
            <h4>Oops! Something went wrong.</h4>
            <p>Unable to load this material right now.</p>
          </div>
        </div>,
      );
    }

    return portalModal(
      <MobileMaterialModal
        key={id}
        handleCloseModal={handleCloseModal}
        onPrimaryAction={() =>
          handleSingle(materialData.vendor, materialData.slug)
        }
        primaryLabel="Similar Materials"
        canEdit={canEdit}
        onEdit={() => handleEdit(materialData.vendor, materialData.slug)}
        viewing={viewing}
        vendor={vendor}
        title={title}
        description={description}
        slug={slug}
        images={materialImages}
        materialData={materialData}
        imageNumber={imageNumber}
        handleChangeImage={handleChangeImage}
        ROOT_SERVER={ROOT_SERVER}
        loaded={loaded}
      />,
    );
  }

  return portalModal(
    <>
      {!error ? (
        <div className={modalStyle.modalShade} onClick={handleCloseModal}>
          {loaded ? (
            <MaterialDetailLayout
              materialData={materialData}
              ROOT_SERVER={ROOT_SERVER}
              viewing={viewing}
              imageNumber={imageNumber}
              onChangeImage={handleChangeImage}
              imageLoaded={imageLoaded}
              onImageLoaded={handleImageLoaded}
              onClose={handleCloseModal}
              closeLabel="close"
              onEdit={() => handleEdit(materialData.vendor, materialData.slug)}
              canEdit={canEdit}
              onCollectionAction={() => changeCollection(materialData.id)}
              collectionLabel={
                inCollection ? "In Collection" : "Add to Collection"
              }
              collectionActive={inCollection}
              onPrimaryAction={() =>
                handleSingle(materialData.vendor, materialData.slug)
              }
              primaryLabel="More like this article"
              onSecondaryAction={() =>
                handleDownloadImage(materialData.vendor, materialData.slug)
              }
              secondaryLabel="Download Image"
              secondaryLoadingLabel="Downloading"
              secondaryIsLoading={imageDownload}
              brandData={brandData}
              showBrandData={true}
              mode="modal"
              onContainerClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className={modalStyle.modalStateCard}>
              <LoadingDots
                label="Loading Material Details..."
                labelClassName={modalStyle.modalStateTitle}
                dotsClassName={modalStyle.modalStateDots}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={modalStyle.modalShade} onClick={handleCloseModal}>
          <div className={modalStyle.modalStateCard}>
            <h4>Oops! Something went wrong.</h4>
            <p>Unable to load this material right now.</p>
          </div>
        </div>
      )}
    </>,
  );
}

export default MaterialModal;
